<template>
  <div>
    <!-- Admin toolbar -->
    <div class="cpt-toolbar" v-if="canEdit">
      <b-button variant="vb-primary" size="sm" pill @click="openAddModal">
        <font-awesome-icon :icon="['fas', 'plus']" /> Add Photo Timeline Entry
      </b-button>
    </div>

    <section class="py-5 timeline-div" v-if="sortedTimeline.length > 0">
      <ul class="timeline-with-icons">
        <li class="timeline-item mb-5 ml-3" v-for="(item, idx) in sortedTimeline" :key="item.timeline_id || idx">
          <span class="timeline-icon" :class="{ 'text-success': item.construction_status == 'Complete' }">
            <font-awesome-icon :icon="item.construction_status == 'Complete' ? ['fas', 'check'] : ['fas', 'circle']" />
          </span>
          <div class="cpt-item-head">
            <div>
              <h5 class="fw-bold mb-1">{{ item.construction_status }}</h5>
              <p class="text-muted mb-2 fw-bold">{{ formatDate(item.field_construction_photo_date) }}</p>
            </div>
            <div class="cpt-item-actions" v-if="canEdit">
              <b-button variant="outline-secondary" size="sm" @click="openEditModal(item)" title="Edit entry">
                <font-awesome-icon :icon="['fas', 'pen']" />
              </b-button>
              <b-button variant="outline-danger" size="sm" @click="deleteEntry(item)" title="Delete entry">
                <font-awesome-icon :icon="['fas', 'trash']" />
              </b-button>
            </div>
          </div>
          <b-row>
            <b-col cols="6" md="4" v-for="(photo, p_index) in item.construction_photos" :key="p_index" class="mb-3">
              <div class="cpt-thumb">
                <b-img thumbnail fluid :src="photo.thumb" :alt="item.construction_status"
                  @click="previewPhoto({ url: photo.url, status: item.construction_status })"></b-img>
                <button v-if="canEdit" type="button" class="cpt-thumb-remove"
                  title="Remove photo" @click.stop="deletePhoto(item, photo.fid || p_index)">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </button>
              </div>
            </b-col>
          </b-row>
        </li>
      </ul>
    </section>

    <p v-else class="text-muted py-5 px-3">No construction photo timeline entries yet.</p>

    <!-- Preview -->
    <b-modal id="preview-modal" :title="previewedPhoto.status" @hidden="previewedPhoto = { status: '', src: null }">
      <b-img :src="previewedPhoto.src" fluid-grow alt="Construction photo"></b-img>
    </b-modal>

    <!-- Add / edit -->
    <b-modal id="cpt-form-modal" :title="form.timeline_id ? 'Edit Timeline Entry' : 'Add Timeline Entry'"
      :ok-title="form.timeline_id ? 'Save' : 'Add'" :ok-disabled="saving" :cancel-disabled="saving"
      @ok.prevent="onSubmit" @hidden="resetForm">
      <b-form-group label="Photo date" label-for="cpt-date">
        <b-form-datepicker id="cpt-date" v-model="form.date" :max="today" placeholder="Select a date"
          reset-button></b-form-datepicker>
      </b-form-group>

      <b-form-group label="Construction status" label-for="cpt-status">
        <b-form-select id="cpt-status" v-model="form.status_tid" :options="statusOptions">
          <template #first>
            <b-form-select-option :value="null">-- Select status --</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <b-form-group :label="form.timeline_id ? 'Add more photos' : 'Photos'" label-for="cpt-files">
        <b-form-file id="cpt-files" v-model="form.files" multiple accept="image/*"
          placeholder="Choose image file(s) or drop them here" drop-placeholder="Drop image file(s) here"
          browse-text="Browse"></b-form-file>
        <small class="text-muted d-block mt-1">You can select multiple photos for the same date.</small>
      </b-form-group>

      <div v-if="form.timeline_id && editingPhotos.length > 0">
        <label class="d-block mb-2">Current photos</label>
        <div class="cpt-existing">
          <div class="cpt-existing-item" v-for="photo in editingPhotos" :key="photo.fid">
            <img :src="photo.thumb" :alt="'Photo ' + photo.fid" />
          </div>
        </div>
        <small class="text-muted d-block mt-1">Remove a photo from the timeline view.</small>
      </div>

      <p v-if="error" class="text-danger mt-3 mb-0">{{ error }}</p>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'pinia';
import { BACKEND_URL } from '../variables';
import { useUserStore } from '../store/UserStore';
import { CONSTRUCTION_STATUS_OPTIONS } from '../data/utcFieldOptions';

export default {
  name: 'ProjectTimeline',
  props: {
    construction_photo_timeline_result: null,
    // UTC field-collection item id; falls back to the route's `uid` query param.
    uid: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      previewedPhoto: {
        src: null,
        status: ""
      },
      statuses: CONSTRUCTION_STATUS_OPTIONS,
      saving: false,
      error: null,
      form: {
        timeline_id: null,
        orig_date: null,
        date: null,
        status_tid: null,
        files: []
      },
      editingPhotos: []
    }
  },
  computed: {
    ...mapState(useUserStore, ["current_user"]),
    canEdit() {
      const role = this.current_user && this.current_user.role;
      if (!role) return false;
      const roles = Array.isArray(role) ? role.join(',') : String(role);
      return roles.includes('admin') || roles.includes('editor');
    },
    utcId() {
      return this.uid || this.$route.query.uid;
    },
    today() {
      return new Date();
    },
    sortedTimeline() {
      const list = Array.isArray(this.construction_photo_timeline_result)
        ? this.construction_photo_timeline_result
        : [];
      return list
        .slice()
        .sort((a, b) => new Date(b.field_construction_photo_date) - new Date(a.field_construction_photo_date));
    },
    statusOptions() {
      return this.statuses.map(s => ({ value: s.tid, text: s.name }));
    }
  },
  mounted() {
    if (this.canEdit) this.fetchStatuses();
  },
  methods: {
    formatDate(dateVal) {
      const date = new Date(dateVal)
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Intl.DateTimeFormat('en-US', options).format(date)
    },
    previewPhoto({ url, status }) {
      this.previewedPhoto = {
        src: url,
        status: status
      }
      this.$nextTick(() => {
        this.$bvModal.show("preview-modal")
      })
    },
    async fetchStatuses() {
      try {
        const res = await axios.get(`${BACKEND_URL}api/construction_timeline/statuses`);
        // Keep the bundled defaults if the endpoint is missing or answers empty.
        if (Array.isArray(res.data) && res.data.length) this.statuses = res.data;
      } catch (e) {
        console.error('Failed to load construction statuses', e);
      }
    },
    resetForm() {
      this.form = { timeline_id: null, orig_date: null, date: null, status_tid: null, files: [] };
      this.editingPhotos = [];
      this.error = null;
      this.saving = false;
    },
    openAddModal() {
      this.resetForm();
      this.$bvModal.show('cpt-form-modal');
    },
    openEditModal(item) {
      this.resetForm();
      this.form.timeline_id = item.timeline_id;
      this.form.orig_date = item.field_construction_photo_date
        ? String(item.field_construction_photo_date).slice(0, 10)
        : null;
      this.form.date = this.form.orig_date;
      this.form.status_tid = item.construction_status_tid ? Number(item.construction_status_tid) : null;
      this.editingPhotos = Object.keys(item.construction_photos || {})
        .map(fid => ({ fid, ...item.construction_photos[fid] }));
      this.$bvModal.show('cpt-form-modal');
    },
    // Every write names its target the same way: an explicit timeline_id when the
    // read payload carried one, otherwise the host UTC plus the entry's date.
    appendLocator(formData, timelineId, date) {
      if (timelineId) formData.append('timeline_id', timelineId);
      if (this.utcId) formData.append('uid', this.utcId);
      if (!timelineId && date) formData.append('date', date);
      return !!(timelineId || (this.utcId && date));
    },
    entryDate(item) {
      return item.field_construction_photo_date
        ? String(item.field_construction_photo_date).slice(0, 10)
        : null;
    },
    async onSubmit() {
      this.error = null;

      if (!this.form.date) {
        this.error = 'Please select a photo date.';
        return;
      }
      const isEdit = !!(this.form.timeline_id || this.form.orig_date);
      if (!isEdit && (!this.form.files || this.form.files.length === 0)) {
        this.error = 'Please choose at least one photo.';
        return;
      }
      if (!this.form.timeline_id && !this.utcId) {
        this.error = 'Missing project reference — reload the page and try again.';
        return;
      }

      this.saving = true;
      try {
        const formData = new FormData();
        if (this.form.timeline_id) formData.append('timeline_id', this.form.timeline_id);
        if (this.utcId) formData.append('uid', this.utcId);
        // orig_date finds the entry when no timeline_id is known, so the date itself
        // can still be edited.
        if (!this.form.timeline_id && this.form.orig_date) formData.append('orig_date', this.form.orig_date);
        formData.append('date', this.form.date);
        formData.append('status_tid', this.form.status_tid === null ? '' : this.form.status_tid);
        (this.form.files || []).forEach(file => formData.append('files[]', file));

        const result = await axios.post(`${BACKEND_URL}api/construction_timeline/add_update`, formData);
        if (result.data && result.data.status === 'error') {
          this.error = result.data.message;
          this.saving = false;
          return;
        }

        this.makeToast('Success', this.form.timeline_id ? 'Timeline entry updated.' : 'Timeline entry added.', 'success');
        this.$bvModal.hide('cpt-form-modal');
        this.$emit('fetchData');
      } catch (e) {
        console.error('Failed to save timeline entry', e);
        this.error = 'Failed to save the timeline entry.';
      } finally {
        this.saving = false;
      }
    },
    async deleteEntry(item) {
      if (!confirm(`Delete the ${this.formatDate(item.field_construction_photo_date)} timeline entry and its photos?`)) return;
      try {
        const formData = new FormData();
        if (!this.appendLocator(formData, item.timeline_id, this.entryDate(item))) {
          this.makeToast('Error', 'Missing project reference — reload the page and try again.', 'danger');
          return;
        }
        const result = await axios.post(`${BACKEND_URL}api/construction_timeline/delete`, formData);
        if (result.data && result.data.status === 'error') {
          this.makeToast('Error', result.data.message, 'danger');
          return;
        }
        this.makeToast('Deleted', 'Timeline entry removed.', 'success');
        this.$emit('fetchData');
      } catch (e) {
        console.error('Failed to delete timeline entry', e);
        this.makeToast('Error', 'Failed to delete the timeline entry.', 'danger');
      }
    },
    async deletePhoto(item, fid) {
      if (!confirm('Remove this photo?')) return;
      try {
        const formData = new FormData();
        if (!this.appendLocator(formData, item.timeline_id, this.entryDate(item))) {
          this.makeToast('Error', 'Missing project reference — reload the page and try again.', 'danger');
          return;
        }
        formData.append('fid', fid);
        const result = await axios.post(`${BACKEND_URL}api/construction_timeline/delete_photo`, formData);
        if (result.data && result.data.status === 'error') {
          this.makeToast('Error', result.data.message, 'danger');
          return;
        }
        this.makeToast('Deleted', 'Photo removed.', 'success');
        this.$emit('fetchData');
      } catch (e) {
        console.error('Failed to remove photo', e);
        this.makeToast('Error', 'Failed to remove the photo.', 'danger');
      }
    },
    makeToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        variant: variant,
        solid: true,
        autoHideDelay: 3000,
        appendToast: true,
        toaster: 'b-toaster-top-right'
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.timeline-with-icons {
  border-left: 1px solid hsl(0, 0%, 90%);
  position: relative;
  list-style: none;
}

.timeline-with-icons .timeline-item {
  position: relative;
}

.timeline-with-icons .timeline-item:after {
  position: absolute;
  display: block;
  top: 0;
}

.timeline-with-icons .timeline-icon {
  position: absolute;
  left: -48px;
  border-radius: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.timeline-div {
  width: 90%;
  float: right;
}

.cpt-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 0.5rem 0;
}

.cpt-item-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.cpt-item-actions {
  display: flex;
  gap: 0.35rem;
}

.cpt-thumb {
  position: relative;
}

.cpt-thumb img {
  cursor: pointer;
}

.cpt-thumb-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  line-height: 1;
  border: none;
  border-radius: var(--rpd-r-pill);
  background: var(--rpd-coral-dark);
  color: var(--rpd-white);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}

.cpt-thumb:hover .cpt-thumb-remove,
.cpt-thumb-remove:focus {
  opacity: 1;
}

.cpt-existing {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cpt-existing-item img {
  width: 72px;
  height: 60px;
  object-fit: cover;
  border-radius: var(--rpd-r-sm);
  border: 1px solid var(--rpd-line);
}

@media (max-width: 767.98px) {
  .timeline-div {
    width: 100%;
    float: none;
    padding-left: 3rem;
  }

  .cpt-toolbar {
    justify-content: flex-start;
  }
}

::v-deep html.dark .timeline-with-icons,
:global(html.dark) .timeline-with-icons {
  border-left-color: var(--rpd-navy-600);
}
</style>
